import React, { useState, useEffect, useMemo } from "react";
import { EyeIcon, EyeOffIcon, Lock, CheckCircle } from "lucide-react";
import { FormField, FormService } from "@shared/services/FormServices";
import { useToast } from "../../../../shared/hooks/use-toast";
import { Button } from "../../../../shared/ui/button";
import { DynamicForm } from "../../../../shared/components/DynamicForm";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "@features/auth/api";
import ImageSection from "./ImageSection";
import { FIELD_CLASSES } from "@shared/utils/constants";

// Password Requirements Component
const PasswordRequirements = ({ password }: { password: string }) => {
  const hasLowerCase = /[a-z]/.test(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const isLongEnough = password.length >= 8;

  const requirements = [
    { met: isLongEnough, text: "At least 8 characters" },
    { met: hasLowerCase, text: "At least one lowercase letter (a-z)" },
    { met: hasUpperCase, text: "At least one uppercase letter (A-Z)" },
    { met: hasNumbers, text: "At least one number (0-9)" },
    {
      met: hasSpecialChars,
      text: "At least one special character (!@#$%^&*)",
      optional: true,
    },
  ];

  return (
    <div className="mt-3 p-3 bg-gray-50 rounded-md border border-gray-200">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">
        Password requirements:
      </h3>
      <div className="space-y-2">
        {requirements.map((req, index) => (
          <div key={index} className="flex items-center space-x-2 text-sm">
            {req.met ? (
              <CheckCircle className="h-4 w-4 text-green-500" />
            ) : req.optional ? (
              <EyeOffIcon className="h-4 w-4 text-yellow-500" />
            ) : (
              <Lock className="h-4 w-4 text-red-500" />
            )}
            <span
              className={
                req.met
                  ? "text-green-600"
                  : req.optional
                  ? "text-yellow-600"
                  : "text-gray-600"
              }
            >
              {req.text}
              {req.optional && (
                <span className="text-yellow-600 font-medium ml-1">
                  (recommended)
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ResetPassword: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showretype_password, setShowretype_password] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Extract URL parameters
  const email = searchParams.get("email") || "";
  const name = searchParams.get("name") || "";
  const tenantName = searchParams.get("tenant_name") || "";
  const tncAccepted = searchParams.get("tnc_accepted") === "True";
  const et = searchParams.get("et");
  const code = searchParams.get("code");

  // Dynamically construct form fields based on URL parameters
  const resetPasswordFields: FormField[] = useMemo(() => {
    const fields: FormField[] = [];

    // Add name field only if name exists in URL
    if (name) {
      fields.push({
        name: "name",
        type: "text",
        label: "Full Name",
        disabled: true,
        defaultValue: name,
      });
    }

    // Add email field only if email exists in URL
    if (email) {
      fields.push({
        name: "email",
        type: "email",
        label: "Email",
        disabled: true,
        defaultValue: email,
      });
    }

    // Add utility service field only if tenant_name exists in URL
    if (tenantName) {
      fields.push({
        name: "utilityService",
        type: "text",
        label: "Utility Service",
        disabled: true,
        defaultValue: tenantName,
      });
    }

    // Always add password fields
    fields.push(
      {
        name: "password",
        type: "password",
        label: "Password",
        required: true,
        placeholder: "Enter your new password",
        minLength: 8,
        defaultValue: "",
        customValidation: (value: string) => {
          if (!value) return true;
          const hasLowerCase = /[a-z]/.test(value);
          const hasUpperCase = /[A-Z]/.test(value);
          const hasNumbers = /\d/.test(value);
          const isLongEnough = value.length >= 8;
          if (!hasLowerCase)
            return "Password must contain at least one lowercase letter";
          if (!hasUpperCase)
            return "Password must contain at least one uppercase letter";
          if (!hasNumbers) return "Password must contain at least one number";
          if (!isLongEnough) return "Password must be at least 8 characters";
          return true;
        },
        fullWidth: true,
      },
      {
        name: "retype_password",
        type: "password",
        label: "Confirm Password",
        required: true,
        placeholder: "Confirm your new password",
        minLength: 8,
        defaultValue: "",
        customValidation: (value: string) => {
          if (!value) return true;
          return true;
        },
        fullWidth: true,
      }
    );

    return fields;
  }, [name, email, tenantName]);

  // Create form using FormService
  const form = FormService.createForm({
    fields: resetPasswordFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Watch password fields for requirements display and matching
  const password = form.watch("password");
  const retype_password = form.watch("retype_password");

  // Check if passwords match whenever they change
  useEffect(() => {
    if (password && retype_password) {
      setPasswordsMatch(password === retype_password);
    } else {
      setPasswordsMatch(true);
    }
  }, [password, retype_password]);

  // Handle form submission
  const handleSubmit = async (data: {
    password: string;
    retype_password: string;
    email?: string;
    name?: string;
    utilityService?: string;
  }) => {
    console.log("Form submitted with data:", data);
    console.log("Search params:", Object.fromEntries(searchParams.entries()));
    try {
      setLoading(true);
      setError(null);

      // Validate password match
      if (data.password !== data.retype_password) {
        setError("Passwords do not match");
        setLoading(false);
        return;
      }

      // Validate password strength
      const hasLower = /[a-z]/.test(data.password);
      const hasUpper = /[A-Z]/.test(data.password);
      const hasDigit = /\d/.test(data.password);
      const minLength = data.password.length >= 8;

      if (!hasLower || !hasUpper || !hasDigit || !minLength) {
        toast({
          title: "Password Error",
          description:
            "Password must be 8+ characters, and include uppercase, lowercase, and a number.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Call actual resetPassword API
      await resetPassword(
        { password: data.password, email: email },
        { et, code }
      );

      toast({
        title: "Password Reset Successful",
        description: `Your password for ${email} has been updated.`,
      });

      navigate("/login");
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        (err instanceof Error
          ? err.message
          : "An error occurred during password reset");

      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    if (FormService.getFieldError(form, fieldName)) {
      form.clearErrors(fieldName);
    }
  };

  // Handle form reset
  const handleReset = () => {
    form.reset();
    setPasswordsMatch(true);
  };

  // Custom footer with set password button
  const renderCustomFooter = () => (
    <div className="space-y-4 mt-6">
      {/* Password match indicator */}
      {retype_password && (
        <div className="flex items-center space-x-2 text-sm">
          <CheckCircle
            className={`h-4 w-4 ${
              passwordsMatch ? "text-green-500" : "text-red-500"
            }`}
          />
          <span className={passwordsMatch ? "text-green-600" : "text-red-600"}>
            {passwordsMatch ? "Passwords match" : "Passwords do not match"}
          </span>
        </div>
      )}

      {/* Encryption notice */}
      <div className="flex items-center space-x-2 text-sm text-gray-600">
        <Lock className="h-4 w-4 text-gray-400" />
        <span>Your password is securely encrypted</span>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors font-medium text-base"
        disabled={loading || !passwordsMatch || !password || !retype_password}
      >
        {loading ? "Setting Password..." : "Set Password"}
      </Button>
    </div>
  );

  return (
    <div className="flex h-screen">
      <ImageSection />

      {/* Right side - Reset Password form */}
      <div className="w-1/2 bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Create a strong password to secure your account.
            </h2>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Loading Display */}
          {loading && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">
              Setting your new password...
            </div>
          )}

          {/* Using DynamicForm component */}
          <div>
            <DynamicForm
              fields={resetPasswordFields.map((field) => {
                // Override password field types based on show/hide state
                if (field.name === "password") {
                  return {
                    ...field,
                    type: showPassword ? "text" : "password",
                  };
                }
                if (field.name === "retype_password") {
                  return {
                    ...field,
                    type: showretype_password ? "text" : "password",
                  };
                }
                return field;
              })}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={loading}
              config={{
                showProgress: false,
                layout: "vertical",
                spacing: "none",
                submitButtonText: "Set Password",
                showResetButton: false,
                resetOnSubmit: false,
                classes: FIELD_CLASSES,
              }}
              renderCustomFooter={renderCustomFooter}
              className=""
              style={{
                background: "transparent",
                border: "none",
                boxShadow: "none",
              }}
            />

            {/* Password Requirements - Show when password field has content */}
            {password && (
              <div className="mt-2">
                <PasswordRequirements password={password} />
              </div>
            )}

            {/* Confirm Password Error */}
            {retype_password && !passwordsMatch && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                Passwords do not match
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;